/**
 * Source of truth for per-dim `description` and `rubric`. Mirrors the
 * spirit of `axis-weights.ts`: edit here once, every surface (LLM service
 * `CRITERION_DIM_MAP`, frontend demo fixtures, hydrator that refreshes
 * stored evaluations) picks it up.
 *
 * Without this module, the same Estabilidade rubric had to be kept in sync
 * across THREE places — `CRITERION_DIM_MAP` (server), `TENURE_RUBRIC` +
 * DEMO_INTERVIEW/DEMO_NO_INTERVIEW description (shared demo), and the
 * rubric/description JSON frozen into stored `assessment` columns at
 * evaluation time. Drift in the human-facing copy was effectively guaranteed.
 *
 * To rewrite a dim's rubric: edit `DIM_METADATA` here. Fresh evaluations,
 * the demo, and stored evaluations rendered through `refreshLiveMetadata`
 * all reflect the new copy on next read.
 */

import type { EvidenceRubricLevel } from "./three-axis";

export type DimId =
  | "academic"
  | "company_era"
  | "growth_within"
  | "trajectory"
  | "tenure"
  | "founder";

export interface DimMetadata {
  description: string;
  rubric: EvidenceRubricLevel[];
}

export const DIM_METADATA: Record<DimId, DimMetadata> = {
  academic: {
    description: "Força e relevância da formação como proxy de seleção cognitiva.",
    rubric: [
      { level: 1, description: "Sem faculdade." },
      { level: 2, description: "Faculdade de baixa seletividade de ingresso." },
      {
        level: 3,
        description:
          "Top BR em curso não-competitivo, OU faculdade estrangeira respeitada (sem ser top mundial).",
      },
      {
        level: 4,
        description:
          "Top BR em curso competitivo (ITA, USP Eng., Medicina), OU top mundial em curso não-STEM. MBA/PhD compensa gap pequeno.",
      },
      {
        level: 5,
        description:
          "STEM (engenharia, medicina, economia) em top global (Ivy, Oxbridge, Stanford, MIT).",
      },
    ],
  },
  company_era: {
    description: "Qualidade dos empregadores ponderada pelo momento de mercado.",
    rubric: [
      { level: 1, description: "Sem passagem por empresa Tier 1 ou Tier 2." },
      { level: 2, description: "≥ 2 anos numa empresa em momento Tier 2." },
      { level: 3, description: "≥ 4 anos em Tier 2, OU ≥ 2 anos em Tier 1." },
      { level: 4, description: "≥ 4 anos em empresa em momento Tier 1." },
      { level: 5, description: "≥ 8 anos em empresa em momento Tier 1." },
    ],
  },
  growth_within: {
    description:
      "Cadência de promoção interna como sinal de performance sustentada. Subidas que vêm SÓ de mudar de empregador (puro job hopping) não substituem crescimento dentro da mesma empresa.",
    rubric: [
      { level: 1, description: "Promoção média de 4–5 anos na empresa." },
      { level: 2, description: "Promoção média de 3–4 anos." },
      { level: 3, description: "Promoção média de 2–3 anos." },
      { level: 4, description: "Promoção média de 1–2 anos." },
      {
        level: 5,
        description: "Dupla promoção numa mesma empresa, OU promoção consistente todo ano.",
      },
    ],
  },
  trajectory: {
    description:
      "Trajetória combinada: qualidade dos empregadores ponderada pelo momento de mercado e cadência de crescimento dentro deles.",
    rubric: [
      {
        level: 1,
        description: "Sem passagem por empresa Tier 1/2 e sem progressão interna visível.",
      },
      {
        level: 2,
        description:
          "≥ 2 anos em Tier 2 ou progressão lenta (4–5 anos por promoção) em qualquer Tier.",
      },
      {
        level: 3,
        description: "≥ 4 anos em Tier 2 ou ≥ 2 anos em Tier 1, com cadência de promoção 2–3 anos.",
      },
      { level: 4, description: "≥ 4 anos em Tier 1, com promoção a cada 1–2 anos." },
      {
        level: 5,
        description:
          "≥ 8 anos em Tier 1 com promoção consistente, OU dupla promoção dentro da mesma empresa.",
      },
    ],
  },
  tenure: {
    description:
      "Concentração de carreira nas duas permanências mais longas — backbone vs. fragmentação, com guard-rails para lock-in e carreira jovem.",
    rubric: [
      {
        level: 1,
        description: "Concentração < 25% — saídas precoces e job hopping sem progressão clara.",
      },
      {
        level: 2,
        description:
          "Concentração 25–40% — histórico fragmentado, sem âncora consistente em qualquer empresa.",
      },
      {
        level: 3,
        description:
          "Concentração 40–80% — permanências razoáveis sem âncora longa, OU carreira < 5 anos (sem tempo para confirmar o padrão), OU lock-in cap (≥ 10 anos numa empresa sem progressão).",
      },
      {
        level: 4,
        description:
          "Concentração ≥ 80% com 5+ anos de carreira — backbone claro e sustentado em até duas empresas.",
      },
      {
        level: 5,
        description:
          "Concentração ≥ 90% com 5+ anos de carreira; ou ≥ 8 anos numa única empresa com 2+ promoções/movimentos internos visíveis.",
      },
    ],
  },
  founder: {
    description: "Experiência fundando ou operando em early-stage.",
    rubric: [
      { level: 1, description: "Nunca trabalhou enquanto a empresa era pequena." },
      { level: 2, description: "Entre os primeiros ~100 funcionários (early-stage employee)." },
      { level: 3, description: "Entre os primeiros ~50 funcionários (early hire)." },
      {
        level: 4,
        description:
          "Entre os primeiros ~20 funcionários (founding team), OU fundou e tocou ≥ 2 anos.",
      },
      {
        level: 5,
        description: "Fundou e tocou > 3 anos, OU fundou múltiplas empresas (cada uma ≥ 1,5 anos).",
      },
    ],
  },
};

/** Look up live metadata for a dim id, or `null` if unknown. */
export function dimMetadata(id: string): DimMetadata | null {
  return id in DIM_METADATA ? DIM_METADATA[id as DimId] : null;
}

/**
 * Historical defaults the system has shipped for each dim's `description`.
 * Used by the read-time hydrator to decide whether a stored evidence's
 * `description` is a generic default that should be refreshed, or a
 * per-candidate rationale that should be preserved.
 *
 * When you rewrite a description in `DIM_METADATA`, append the OLD copy
 * here so old stored evaluations keep refreshing instead of being
 * mistaken for rationales.
 */
export const DIM_DESCRIPTION_HISTORY: Record<DimId, readonly string[]> = {
  academic: [],
  company_era: ["Qualidade dos empregadores ponderada pelo momento de mercado naquela empresa."],
  growth_within: [
    "Cadência de promoção interna como sinal de performance sustentada.",
    "Eventos de crescimento intra-empresa (promoções e crescimentos de escopo). Conta o evento, não o ritmo — tenure é medido por outro eixo.",
  ],
  trajectory: [],
  tenure: [
    "Permanência em empresas como sinal de comprometimento e ownership.",
    "Permanência em empresas como sinal de comprometimento e cultura de ownership.",
  ],
  founder: ["Experiência fundando ou operando em early-stage como proxy de autonomia e ambição."],
};

/** True if `description` looks like a historical or current default for `id` —
 *  i.e. safe to overwrite with the current default during read-time refresh. */
export function isDefaultDescription(id: DimId, description: string): boolean {
  if (!description) return true;
  if (description === DIM_METADATA[id].description) return true;
  return DIM_DESCRIPTION_HISTORY[id].includes(description);
}
